import type { LucideIcon } from 'lucide-react'

import type { FilterDefinition } from '@/components/filters/list-filter-types'
import type { FilterOption } from '@/components/filters/filter-menu'
import {
  facetsToSkillFilterOptions,
  selectedSkillKeysFromLabels,
  skillLabelsFromKeys,
} from '@/components/filters/skill-filter-menu-content'
import {
  formatFreeTextChipDisplay,
  formatMultiValueChipDisplay,
  formatMultiValueChipDisplayFromLabels,
} from '@/lib/list-filters/list-filter-params'
import type { TalentFacet } from '@/lib/talent-browse-filters'

type UnifiedMultiFilterConfig<TFilters, _TCtx> = {
  key: string
  label: string
  icon?: LucideIcon
  options: FilterOption[]
  labels: Record<string, string>
  getValues: (filters: TFilters) => string[]
  setValues: (filters: TFilters, values: string[]) => TFilters
  searchPlaceholder?: string
  emptyLabel?: string
  /** Strip pseudo-options such as employer `all` from picker lists. */
  excludeOptionValues?: readonly string[]
}

type UnifiedSingleFilterConfig<TFilters, _TCtx> = Omit<
  UnifiedMultiFilterConfig<TFilters, _TCtx>,
  'getValues' | 'setValues'
> & {
  getValue: (filters: TFilters) => string
  setValue: (filters: TFilters, value: string) => TFilters
  emptyLabel?: string
}

type TextContainsFilterConfig<TFilters, _TCtx> = {
  key: string
  label: string
  icon: LucideIcon
  getValue: (filters: TFilters) => string
  setValue: (filters: TFilters, value: string) => TFilters
  inputPlaceholder: string
}

function pickerOptions(
  options: FilterOption[],
  excludeOptionValues: readonly string[] = [],
): FilterOption[] {
  if (excludeOptionValues.length === 0) return options
  const excluded = new Set(excludeOptionValues)
  return options.filter((option) => !excluded.has(option.value))
}

/** Multi-select enum chip — shared by candidate jobs, employer jobs, and future lists. */
export function buildUnifiedMultiFilterDef<TFilters, TCtx>(
  config: UnifiedMultiFilterConfig<TFilters, TCtx>,
): FilterDefinition<TFilters, TCtx> {
  const options = pickerOptions(config.options, config.excludeOptionValues)

  return {
    key: config.key,
    label: config.label,
    icon: config.icon,
    displayValue: (filters) => formatMultiValueChipDisplay(config.getValues(filters), config.labels),
    isNonDefault: (filters) => config.getValues(filters).length > 0,
    reset: (filters) => config.setValues(filters, []),
    unified: {
      selection: 'multi',
      optionsSource: 'static',
      searchPlaceholder: config.searchPlaceholder,
      emptyLabel: config.emptyLabel,
      getOptions: () => options,
      getSelectedValues: (filters) => config.getValues(filters),
      setSelectedValues: (filters, values) => config.setValues(filters, values),
    },
  }
}

/** Single-select enum chip — shared picker shell. */
export function buildUnifiedSingleFilterDef<TFilters, TCtx>(
  config: UnifiedSingleFilterConfig<TFilters, TCtx>,
): FilterDefinition<TFilters, TCtx> {
  const options = pickerOptions(config.options, config.excludeOptionValues)

  return {
    key: config.key,
    label: config.label,
    icon: config.icon,
    displayValue: (filters) => {
      const value = config.getValue(filters)
      return value ? (config.labels[value] ?? value) : 'Any'
    },
    isNonDefault: (filters) => config.getValue(filters).length > 0,
    reset: (filters) => config.setValue(filters, ''),
    unified: {
      selection: 'single',
      optionsSource: 'static',
      searchPlaceholder: config.searchPlaceholder,
      emptyLabel: config.emptyLabel,
      getOptions: () => options,
      getSelectedValues: (filters) => {
        const value = config.getValue(filters)
        return value ? [value] : []
      },
      setSelectedValues: (filters, values) => config.setValue(filters, values[0] ?? ''),
    },
  }
}

type FacetedSkillsFilterConfig<TFilters, TCtx> = {
  key?: string
  label?: string
  icon: LucideIcon
  getSelectedSkills: (filters: TFilters) => string[]
  setSelectedSkills: (filters: TFilters, skills: string[]) => TFilters
  getSkillFacets: (ctx: TCtx) => TalentFacet[]
  reset: (filters: TFilters) => TFilters
  isPickerCatalogLoading?: (ctx: TCtx) => boolean
  searchPlaceholder?: string
  emptyLabel?: string
}

/** Faceted multi-select skills chip — shared by candidate jobs, job applicants tracking, etc. */
export function buildFacetedSkillsFilterDef<TFilters, TCtx>(
  config: FacetedSkillsFilterConfig<TFilters, TCtx>,
): FilterDefinition<TFilters, TCtx> {
  const key = config.key ?? 'skills'
  const label = config.label ?? 'Skills'

  return {
    key,
    label,
    icon: config.icon,
    displayValue: (filters) => formatMultiValueChipDisplayFromLabels(config.getSelectedSkills(filters)),
    chipValueTitle: (filters) => {
      const skills = config.getSelectedSkills(filters)
      return skills.length > 0 ? skills.join(', ') : undefined
    },
    isNonDefault: (filters) => config.getSelectedSkills(filters).length > 0,
    reset: config.reset,
    unified: {
      selection: 'multi',
      optionsSource: 'faceted',
      typeaheadValues: false,
      searchPlaceholder: config.searchPlaceholder ?? 'Search skills…',
      emptyLabel: config.emptyLabel ?? 'No skills found',
      getOptions: (ctx) => facetsToSkillFilterOptions(config.getSkillFacets(ctx)),
      isPickerCatalogLoading: config.isPickerCatalogLoading,
      getSelectedValues: (filters) => selectedSkillKeysFromLabels(config.getSelectedSkills(filters)),
      setSelectedValues: (filters, values, ctx) =>
        config.setSelectedSkills(
          filters,
          skillLabelsFromKeys(values, config.getSkillFacets(ctx), config.getSelectedSkills(filters)),
        ),
    },
  }
}

/** Text-contains dimension edited inline in the unified field (not folded into keyword `q`). */
export function buildTextContainsFilterDef<TFilters, TCtx>(
  config: TextContainsFilterConfig<TFilters, TCtx>,
): FilterDefinition<TFilters, TCtx> {
  return {
    key: config.key,
    label: config.label,
    icon: config.icon,
    displayValue: (filters) => {
      const value = config.getValue(filters).trim()
      return value ? formatFreeTextChipDisplay(value) : 'Any'
    },
    chipValueTitle: (filters) => {
      const value = config.getValue(filters).trim()
      return value || undefined
    },
    isNonDefault: (filters) => config.getValue(filters).trim().length > 0,
    reset: (filters) => config.setValue(filters, ''),
    unified: {
      selection: 'single',
      valueEntry: 'inlineText',
      inlineTextPlaceholder: config.inputPlaceholder,
      typeaheadValues: false,
      getOptions: () => [],
      getSelectedValues: (filters) => {
        const value = config.getValue(filters).trim()
        return value ? [value] : []
      },
      setSelectedValues: (filters, values) => config.setValue(filters, values[0] ?? ''),
    },
  }
}
